import React from "react";
import Footer from "../components/Footer";

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <h2 className="text-lg font-medium text-zinc-100 mb-2 pb-1 border-b border-zinc-700/50">
      {title}
    </h2>
    {children}
  </div>
);

const PrivacyPolicyPage: React.FC = () => (
  <div className="min-h-screen bg-[#0a0b0f] flex flex-col">
    <div className="py-8 px-4 flex-1">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold text-zinc-100">Privacy Policy</h1>
          <p className="text-zinc-500 mt-2 text-sm">Last Updated: May 2026</p>
        </div>

        <div className="card-dark rounded-xl p-8 space-y-6 text-zinc-300 text-sm leading-relaxed">
          <p>
            This Privacy Policy describes how we collect, use, and protect your information when you
            use our services. By using our services, you consent to the practices described in this
            policy.
          </p>

          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
            <h3 className="text-amber-300 font-medium mb-1">We Do Not Sell Your Information</h3>
            <p className="text-zinc-300">
              We do not sell, rent, or trade your personal information to third parties for any
              purpose. Information you provide is used only to operate the services you use, and is
              shared only with the limited service providers necessary to deliver those services
              (described below).
            </p>
          </div>

          <Section title="1. Information We Collect">
            <h4 className="text-zinc-200 font-medium mt-3 mb-1">Account Information</h4>
            <ul className="list-disc ml-5 space-y-1 text-zinc-400">
              <li>Name, email address, and phone number</li>
              <li>Login credentials and authentication tokens</li>
              <li>Subscription and billing details</li>
            </ul>
            <h4 className="text-zinc-200 font-medium mt-3 mb-1">Service Data</h4>
            <ul className="list-disc ml-5 space-y-1 text-zinc-400">
              <li>Information you create or upload while using the services (such as photos, notes, records, and documents)</li>
              <li>Location and metadata associated with uploaded content</li>
              <li>Communications you send through the services</li>
            </ul>
            <h4 className="text-zinc-200 font-medium mt-3 mb-1">Usage Information</h4>
            <ul className="list-disc ml-5 space-y-1 text-zinc-400">
              <li>Feature usage and preferences</li>
              <li>Diagnostic and error logs</li>
              <li>Audit records of system activity</li>
            </ul>
          </Section>

          <Section title="2. How We Use Your Information">
            <p className="mb-2">We use your information to:</p>
            <ul className="list-disc ml-5 space-y-1 text-zinc-400">
              <li>Provide, maintain, and improve the services</li>
              <li>Process the data you create and store within the services</li>
              <li>Sync data across your devices when you enable that feature</li>
              <li>Process billing and subscription transactions</li>
              <li>Respond to your support requests</li>
              <li>Maintain audit logs for security and compliance</li>
              <li>Send service-related communications</li>
            </ul>
          </Section>

          <Section title="3. Third-Party Service Providers">
            <p className="mb-2 text-zinc-400">
              To operate the services, we may share limited information with vetted third-party
              providers, including:
            </p>
            <ul className="list-disc ml-5 space-y-1 text-zinc-400">
              <li>A <strong className="text-zinc-300">payment processor</strong> to handle subscription billing</li>
              <li>A <strong className="text-zinc-300">cloud or hosting provider</strong> for service delivery</li>
              <li>A <strong className="text-zinc-300">communications provider</strong> when sending email or text messages on your behalf</li>
              <li><strong className="text-zinc-300">AI or analysis tools</strong> used to provide automated assistance features</li>
              <li><strong className="text-zinc-300">Authentication providers</strong> when you choose to sign in with a third-party account</li>
            </ul>
            <p className="text-zinc-400 mt-3">
              These providers are bound by confidentiality and data-protection obligations and are
              permitted to use your information only as needed to deliver their service to us. We are
              not responsible for the privacy practices of providers you choose to integrate with
              directly.
            </p>
          </Section>

          <Section title="4. Data Storage and Security">
            <ul className="list-disc ml-5 space-y-1 text-zinc-400">
              <li>Data transmitted to and from our servers uses encrypted connections (TLS/HTTPS)</li>
              <li>Sensitive credentials are protected using industry-standard encryption</li>
              <li>We apply reasonable administrative, technical, and physical safeguards to protect your information</li>
            </ul>
            <p className="text-zinc-400 mt-2">
              No method of electronic storage or transmission is 100% secure, and we cannot guarantee
              absolute security.
            </p>
          </Section>

          <Section title="5. Data Retention">
            <ul className="list-disc ml-5 space-y-1 text-zinc-400">
              <li>Information you create within the services is retained until you delete it or close your account</li>
              <li>Audit and communication logs are retained for the period needed to satisfy security, legal, and compliance obligations</li>
              <li>You may request deletion of your data at any time</li>
            </ul>
          </Section>

          <Section title="6. Your Rights">
            <p className="mb-2">You have the right to:</p>
            <ul className="list-disc ml-5 space-y-1 text-zinc-400">
              <li><strong className="text-zinc-300">Access:</strong> request a copy of the personal information we hold about you</li>
              <li><strong className="text-zinc-300">Correction:</strong> request correction of inaccurate information</li>
              <li><strong className="text-zinc-300">Deletion:</strong> request deletion of your information</li>
              <li><strong className="text-zinc-300">Portability:</strong> request an export of data you've created within the services</li>
              <li><strong className="text-zinc-300">Opt-out:</strong> disable optional features</li>
            </ul>
            <p className="text-zinc-400 mt-2">
              To exercise these rights, contact us through the support channels provided in the
              services.
            </p>
          </Section>

          <Section title="7. SMS Communications">
            <p className="mb-2">If you use messaging features within the services:</p>
            <ul className="list-disc ml-5 space-y-1 text-zinc-400">
              <li>Messages are sent only to recipients with whom you have a legitimate business relationship</li>
              <li>Recipients may opt out at any time by replying STOP</li>
              <li>Opt-out requests are honored immediately</li>
              <li>Message logs are retained for compliance purposes</li>
            </ul>
          </Section>

          <Section title="8. Children's Privacy">
            <p className="text-zinc-400">
              Our services are intended for professional use by adults. We do not knowingly collect
              information from children under 18.
            </p>
          </Section>

          <Section title="9. California Privacy Rights (CCPA)">
            <p className="mb-2 text-zinc-400">
              If you are a California resident, you have additional rights under the California
              Consumer Privacy Act:
            </p>
            <ul className="list-disc ml-5 space-y-1 text-zinc-400">
              <li>Right to know what categories of personal information are collected</li>
              <li>Right to know whether personal information is sold or disclosed (we do not sell your data)</li>
              <li>Right to request deletion of your personal information</li>
              <li>Right to non-discrimination for exercising your rights</li>
            </ul>
          </Section>

          <Section title="10. Changes to This Policy">
            <p className="text-zinc-400">
              We may update this Privacy Policy from time to time. We will notify you of significant
              changes through the services. Continued use after changes constitutes acceptance of the
              updated policy.
            </p>
          </Section>

          <Section title="11. Contact Us">
            <p className="text-zinc-400">
              For questions about this Privacy Policy or to exercise your data rights, please contact
              us through the support channels provided in the services.
            </p>
          </Section>
        </div>

        <div className="mt-6 text-center">
          <a
            href="/"
            className="text-amber-400 hover:text-amber-300 text-sm hover:underline"
          >
            Back to Home
          </a>
        </div>
      </div>
    </div>
    <Footer />
  </div>
);

export default PrivacyPolicyPage;
